import React, { useState, useEffect } from "react";
import {
  Clock,
  CheckCircle,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  Edit2,
  UserCheck,
  BookOpen,
  Filter,
  AlertTriangle,
  Info,
} from "lucide-react";
import { Answer, SmeTask, UserProfile, AppSettings } from "../../types";
import {
  subscribeToAnswers,
  verifyAnswerFreshness,
  updateAnswer,
  deleteAnswer,
  calculateFreshness,
} from "../../services/answersService";
import {
  subscribeToUserSmeTasks,
  submitSmeTaskResponse,
  acceptSmeTask,
  returnSmeTask,
} from "../../services/smeTasksService";
import { subscribeToSettings, DEFAULT_APP_SETTINGS } from "../../services/settingsService";
import { useStaffDirectory } from "../../services/staffService";
import { runComprehensiveFreshnessScan } from "../../utils/freshnessScanner";
import { auth } from "../../lib/firebase";
import { AnswerModal } from "../library/AnswerModal";
import { CopyNarrativeButton } from "../library/CopyNarrativeButton";
import { SmeTaskCard } from "../workspace/SmeTaskCard";
import { ExpandableCard } from "../common/ExpandableCard";

interface ReviewQueueViewProps {
  user?: UserProfile | null;
  initialTab?: "assigned" | "library" | "freshness";
}

export const ReviewQueueView: React.FC<ReviewQueueViewProps> = ({
  user,
  initialTab = "assigned",
}) => {
  const [activeTab, setActiveTab] = useState<"assigned" | "library" | "freshness">(initialTab);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [smeTasks, setSmeTasks] = useState<SmeTask[]>([]);
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_APP_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [editingAnswer, setEditingAnswer] = useState<Answer | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filters
  const [smeFilter, setSmeFilter] = useState<"all" | "pending" | "submitted" | "accepted">("all");
  const [freshnessFilter, setFreshnessFilter] = useState<"all" | "high" | "medium" | "low">("all");

  const { staff } = useStaffDirectory();
  const effectiveEmail = user?.email || auth.currentUser?.email || "";

  // Subscribe to library answers
  useEffect(() => {
    const unsubscribe = subscribeToAnswers(
      (items) => {
        setAnswers(items);
        setLoading(false);
      },
      (err) => {
        console.error("Failed to load answers for review queue:", err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  // Subscribe to settings
  useEffect(() => {
    const unsubscribe = subscribeToSettings((s) => setSettings(s));
    return () => unsubscribe();
  }, []);

  // Subscribe to user SME tasks
  useEffect(() => {
    if (!effectiveEmail) {
      return;
    }

    const unsubscribe = subscribeToUserSmeTasks(
      effectiveEmail,
      (tasks) => setSmeTasks(tasks),
      (err) => console.error("Failed to load SME tasks for user:", err),
    );

    return () => {
      unsubscribe();
      setSmeTasks([]);
    };
  }, [effectiveEmail]);

  // Library queue metrics
  const queueItems = answers.filter((a) => {
    if (a.status === "retired") return false;
    const f = calculateFreshness(a.reviewDueDate, a.status);
    return f.isOverdue || f.isDueSoon;
  });

  const overdueItems = queueItems.filter((a) => {
    const f = calculateFreshness(a.reviewDueDate, a.status);
    return f.isOverdue;
  });

  const dueSoonItems = queueItems.filter((a) => {
    const f = calculateFreshness(a.reviewDueDate, a.status);
    return !f.isOverdue && f.isDueSoon;
  });

  const freshCount = answers.filter((a) => {
    if (a.status === "retired") return false;
    const f = calculateFreshness(a.reviewDueDate, a.status);
    return !f.isOverdue && !f.isDueSoon;
  }).length;

  // SME tasks metrics
  const pendingSmeTasks = smeTasks.filter((t) => t.status === "open" || t.status === "returned");

  const filteredSmeTasks = smeTasks.filter((t) => {
    if (smeFilter === "pending") return t.status === "open" || t.status === "returned";
    if (smeFilter === "submitted") return t.status === "submitted";
    if (smeFilter === "accepted") return t.status === "accepted";
    return true;
  });

  // Freshness scanner findings
  const activeStaffEmails = staff.map((s) => s.email);
  const freshnessFindings = runComprehensiveFreshnessScan({
    answers,
    activeStaffEmails,
    thresholdYears: settings.freshnessSettings?.staleYearThreshold ?? 2,
  });

  const filteredFindings = freshnessFindings.filter((f) => {
    if (freshnessFilter === "all") return true;
    return f.severity === freshnessFilter;
  });

  const highSeverityFindings = freshnessFindings.filter((f) => f.severity === "high");

  // Handlers
  const handleVerify = async (id: string) => {
    try {
      await verifyAnswerFreshness(id, 180);
    } catch (err) {
      console.error("Failed to verify answer:", err);
    }
  };

  const handleSaveEditedAnswer = async (data: Omit<Answer, "id" | "createdAt" | "updatedAt">) => {
    if (editingAnswer) {
      await updateAnswer(editingAnswer.id, data);
    }
  };

  const handleDeleteEditedAnswer = async (id: string) => {
    await deleteAnswer(id);
    setIsModalOpen(false);
    setEditingAnswer(null);
  };

  const handleSubmitSme = async (task: SmeTask, response: string) => {
    await submitSmeTaskResponse(task.opportunityId, task.id, response);
  };

  const handleAcceptSme = async (task: SmeTask) => {
    const resolver = user?.displayName || user?.email || effectiveEmail || "Reviewer";
    await acceptSmeTask(task.opportunityId, task.id, resolver);
  };

  const handleReturnSme = async (task: SmeTask, reason: string) => {
    const resolver = user?.displayName || user?.email || effectiveEmail || "Reviewer";
    await returnSmeTask(task.opportunityId, task.id, reason, resolver);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
          Review & Freshness Queue
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          SME questions routed to you, library answers due for verification, and automated freshness
          scanner findings to prevent outdated assertions in proposals.
        </p>
      </div>

      {/* Top Navigation Tabs */}
      <div className="flex border-b border-slate-200 dark:border-slate-800">
        <button
          type="button"
          onClick={() => setActiveTab("assigned")}
          className={`flex items-center gap-2 border-b-2 px-5 py-3 text-sm font-medium transition-colors ${
            activeTab === "assigned"
              ? "border-[#2ba5b5] text-[#2ba5b5]"
              : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          }`}
        >
          <UserCheck className="h-4 w-4" />
          <span>Assigned to Me</span>
          {pendingSmeTasks.length > 0 && (
            <span className="rounded-full bg-rose-500 px-2 py-0.5 text-[11px] font-bold text-white leading-none">
              {pendingSmeTasks.length}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("library")}
          className={`flex items-center gap-2 border-b-2 px-5 py-3 text-sm font-medium transition-colors ${
            activeTab === "library"
              ? "border-[#2ba5b5] text-[#2ba5b5]"
              : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          }`}
        >
          <BookOpen className="h-4 w-4" />
          <span>Library Reviews Due</span>
          {overdueItems.length > 0 && (
            <span className="rounded-full bg-rose-500 px-2 py-0.5 text-[11px] font-bold text-white leading-none">
              {overdueItems.length}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("freshness")}
          className={`flex items-center gap-2 border-b-2 px-5 py-3 text-sm font-medium transition-colors ${
            activeTab === "freshness"
              ? "border-[#2ba5b5] text-[#2ba5b5]"
              : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          }`}
        >
          <Sparkles className="h-4 w-4" />
          <span>Freshness Findings</span>
          {freshnessFindings.length > 0 && (
            <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[11px] font-bold text-white leading-none">
              {freshnessFindings.length}
            </span>
          )}
        </button>
      </div>

      {/* Tab 1: Assigned to Me */}
      {activeTab === "assigned" && (
        <div className="space-y-5">
          {/* Controls Bar */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Filter:
              </span>
              <div className="flex rounded-lg border border-slate-200 p-0.5 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setSmeFilter("all")}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                    smeFilter === "all"
                      ? "bg-[#2ba5b5] text-white"
                      : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
                >
                  All ({smeTasks.length})
                </button>
                <button
                  type="button"
                  onClick={() => setSmeFilter("pending")}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                    smeFilter === "pending"
                      ? "bg-[#2ba5b5] text-white"
                      : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
                >
                  Pending Response ({pendingSmeTasks.length})
                </button>
                <button
                  type="button"
                  onClick={() => setSmeFilter("submitted")}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                    smeFilter === "submitted"
                      ? "bg-[#2ba5b5] text-white"
                      : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
                >
                  Submitted ({smeTasks.filter((t) => t.status === "submitted").length})
                </button>
                <button
                  type="button"
                  onClick={() => setSmeFilter("accepted")}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                    smeFilter === "accepted"
                      ? "bg-[#2ba5b5] text-white"
                      : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
                >
                  Accepted ({smeTasks.filter((t) => t.status === "accepted").length})
                </button>
              </div>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Tasks routed from proposal workspace requirement matrices
            </p>
          </div>

          {/* List or Empty State */}
          {filteredSmeTasks.length === 0 ? (
            <div className="glass flex flex-col items-center justify-center rounded-2xl border border-slate-200/80 p-12 text-center dark:border-slate-800/80">
              <div className="rounded-full bg-emerald-50 p-3 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400">
                <UserCheck className="h-6 w-6" />
              </div>
              <h3 className="mt-4 text-base font-semibold text-slate-800 dark:text-slate-200">
                No SME Tasks Assigned to You
              </h3>
              <p className="mt-1 max-w-md text-xs text-slate-500 dark:text-slate-400">
                When proposal writers need your specialized translation, pricing, technical, or
                operational expertise, routed questions and evidence requests will appear here.
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredSmeTasks.map((task) => (
                <SmeTaskCard
                  key={task.id}
                  task={task}
                  currentUserEmail={effectiveEmail}
                  showOpportunityContext={true}
                  defaultExpanded={task.status === "open" || task.status === "returned"}
                  onSubmitResponse={handleSubmitSme}
                  onAccept={handleAcceptSme}
                  onReturn={handleReturnSme}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Library Reviews Due */}
      {activeTab === "library" && (
        <div className="space-y-6">
          {/* Metrics Banner */}
          <div className="grid gap-4 sm:grid-cols-3">
            {/* Overdue */}
            <div className="glass rounded-xl p-5 border-l-4 border-l-rose-500">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-rose-50 p-2.5 text-rose-600 dark:bg-rose-950/60 dark:text-rose-400">
                  <ShieldAlert className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
                    Overdue Review
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                    {overdueItems.length}
                  </h3>
                </div>
              </div>
            </div>

            {/* Due Soon */}
            <div className="glass rounded-xl p-5 border-l-4 border-l-amber-500">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-amber-50 p-2.5 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400">
                  <Clock className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
                    Due Within 30 Days
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                    {dueSoonItems.length}
                  </h3>
                </div>
              </div>
            </div>

            {/* Verified / Up to date */}
            <div className="glass rounded-xl p-5 border-l-4 border-l-emerald-500">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-emerald-50 p-2.5 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400">
                  <CheckCircle className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
                    Fresh & Verified
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                    {freshCount}
                  </h3>
                </div>
              </div>
            </div>
          </div>

          {/* Queue Body */}
          {loading ? (
            <div className="glass flex items-center justify-center rounded-2xl p-12 text-slate-400">
              <RefreshCw className="h-6 w-6 animate-spin text-[#2ba5b5]" />
              <span className="ml-3 text-sm">Evaluating library freshness...</span>
            </div>
          ) : queueItems.length === 0 ? (
            <div className="glass flex flex-col items-center justify-center rounded-2xl border border-slate-200/80 p-12 text-center dark:border-slate-800/80">
              <div className="rounded-full bg-emerald-50 p-3 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400">
                <Sparkles className="h-6 w-6" />
              </div>
              <h3 className="mt-4 text-base font-semibold text-slate-800 dark:text-slate-200">
                Review Queue is Clear
              </h3>
              <p className="mt-1 max-w-md text-xs text-slate-500 dark:text-slate-400">
                All active library answers are currently verified within their freshness intervals.
                Responses approaching their expiration dates will appear here automatically.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Items Requiring Freshness Verification ({queueItems.length})
                </h3>
                <span className="text-xs text-slate-400">
                  Click &quot;Verify as Current&quot; to reset the 6-month verification timer
                </span>
              </div>

              <div className="grid gap-4">
                {queueItems.map((ans) => {
                  const freshness = calculateFreshness(ans.reviewDueDate, ans.status);

                  return (
                    <div
                      key={ans.id}
                      className={`glass rounded-xl border p-5 shadow-xs transition-all ${
                        freshness.isOverdue
                          ? "border-rose-300/80 dark:border-rose-900/60 bg-rose-50/20"
                          : "border-amber-300/80 dark:border-amber-900/60 bg-amber-50/20"
                      }`}
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${freshness.badgeColor}`}
                            >
                              {freshness.badgeLabel}
                            </span>
                            <h4 className="text-base font-semibold text-slate-900 dark:text-white">
                              {ans.title}
                            </h4>
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                            <span>
                              Owner:{" "}
                              <strong className="text-slate-700 dark:text-slate-200">
                                {ans.owner}
                              </strong>
                            </span>
                            <span>•</span>
                            <span>Last Verified: {ans.lastVerifiedDate || "Unknown"}</span>
                            <span>•</span>
                            <span>Due Date: {ans.reviewDueDate}</span>
                            {ans.sourceProposal && (
                              <>
                                <span>•</span>
                                <span>Source: {ans.sourceProposal}</span>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <CopyNarrativeButton textToCopy={ans.body} label="Copy" />
                          <button
                            type="button"
                            onClick={() => {
                              setEditingAnswer(ans);
                              setIsModalOpen(true);
                            }}
                            className="btn-animate inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                          >
                            <Edit2 className="h-3.5 w-3.5 text-slate-400" />
                            <span>Edit</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleVerify(ans.id)}
                            className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3.5 py-1.5 text-xs font-semibold text-white shadow-2xs hover:bg-[#238997]"
                          >
                            <CheckCircle className="h-3.5 w-3.5" />
                            <span>Verify & Extend</span>
                          </button>
                        </div>
                      </div>

                      <div className="mt-3 rounded-lg bg-slate-50/80 p-3 text-xs leading-relaxed text-slate-700 dark:bg-slate-800/60 dark:text-slate-300 font-sans line-clamp-3">
                        {ans.body}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab 3: Freshness Findings */}
      {activeTab === "freshness" && (
        <div className="space-y-5">
          {/* Scanner Header Banner */}
          <div className="glass rounded-xl border border-slate-200/80 p-5 dark:border-slate-800/80">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-[#2ba5b5]" />
                  <h3 className="text-base font-bold text-slate-900 dark:text-white">
                    Deterministic Freshness Scanner
                  </h3>
                </div>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Scans library entries for stale historical year citations (&gt;
                  {settings.freshnessSettings?.staleYearThreshold ?? 2} years ago) and unassigned or
                  inactive staff owners according to MLC Staff HQ.
                </p>
              </div>

              {/* Severity Summary Pills */}
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-3 py-1 text-xs font-bold text-rose-800 dark:bg-rose-950/60 dark:text-rose-300">
                  <ShieldAlert className="h-3.5 w-3.5" />
                  {highSeverityFindings.length} High
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {freshnessFindings.filter((f) => f.severity === "medium").length} Medium
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                  <Info className="h-3.5 w-3.5" />
                  {freshnessFindings.filter((f) => f.severity === "low").length} Low
                </span>
              </div>
            </div>

            {/* Severity Filter */}
            <div className="mt-4 flex items-center gap-2 border-t border-slate-200/60 pt-3 dark:border-slate-800/60">
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                Filter Severity:
              </span>
              <div className="flex rounded-lg border border-slate-200 p-0.5 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setFreshnessFilter("all")}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                    freshnessFilter === "all"
                      ? "bg-[#2ba5b5] text-white"
                      : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
                >
                  All ({freshnessFindings.length})
                </button>
                <button
                  type="button"
                  onClick={() => setFreshnessFilter("high")}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                    freshnessFilter === "high"
                      ? "bg-[#2ba5b5] text-white"
                      : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
                >
                  High
                </button>
                <button
                  type="button"
                  onClick={() => setFreshnessFilter("medium")}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                    freshnessFilter === "medium"
                      ? "bg-[#2ba5b5] text-white"
                      : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
                >
                  Medium
                </button>
                <button
                  type="button"
                  onClick={() => setFreshnessFilter("low")}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                    freshnessFilter === "low"
                      ? "bg-[#2ba5b5] text-white"
                      : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
                >
                  Low
                </button>
              </div>
            </div>
          </div>

          {/* Findings List or Empty State */}
          {filteredFindings.length === 0 ? (
            <div className="glass flex flex-col items-center justify-center rounded-2xl border border-slate-200/80 p-12 text-center dark:border-slate-800/80">
              <div className="rounded-full bg-emerald-50 p-3 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400">
                <CheckCircle className="h-6 w-6" />
              </div>
              <h3 className="mt-4 text-base font-semibold text-slate-800 dark:text-slate-200">
                No Freshness Findings
              </h3>
              <p className="mt-1 max-w-md text-xs text-slate-500 dark:text-slate-400">
                All scanned library answers have verified active owners and current calendar year
                references.
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredFindings.map((finding) => {
                const badgeColor =
                  finding.severity === "high"
                    ? "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300"
                    : finding.severity === "medium"
                      ? "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300"
                      : "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300";

                const typeLabel =
                  finding.type === "stale_year"
                    ? "Stale Year Citation"
                    : finding.type === "inactive_owner"
                      ? "Inactive Owner"
                      : finding.type === "retired_answer"
                        ? "Retired Citation"
                        : "Review Due Citation";

                const targetAnswer =
                  finding.targetType === "library_answer"
                    ? answers.find((a) => a.id === finding.targetId)
                    : null;

                return (
                  <ExpandableCard
                    key={finding.id}
                    title={finding.targetTitle}
                    badge={`${finding.severity.toUpperCase()} • ${typeLabel}`}
                    badgeColor={badgeColor}
                    defaultExpanded={false}
                    summary={
                      <p className="pt-1 text-xs text-slate-600 dark:text-slate-300 line-clamp-1">
                        {finding.details}
                      </p>
                    }
                  >
                    <div className="space-y-3 pt-2 text-xs">
                      <div>
                        <span className="font-semibold text-slate-500 dark:text-slate-400">
                          Finding Details:
                        </span>
                        <p className="mt-0.5 text-slate-700 dark:text-slate-300">
                          {finding.details}
                        </p>
                      </div>

                      <div>
                        <span className="font-semibold text-slate-500 dark:text-slate-400">
                          Suggested Action:
                        </span>
                        <p className="mt-0.5 text-slate-700 dark:text-slate-300">
                          {finding.suggestedAction}
                        </p>
                      </div>

                      {targetAnswer && (
                        <div className="flex items-center gap-2 pt-2 border-t border-slate-200/60 dark:border-slate-800/60">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingAnswer(targetAnswer);
                              setIsModalOpen(true);
                            }}
                            className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3.5 py-1.5 text-xs font-semibold text-white shadow-2xs hover:bg-[#238997]"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                            <span>Edit Library Answer</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </ExpandableCard>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Edit Modal */}
      {editingAnswer && (
        <AnswerModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingAnswer(null);
          }}
          onSave={handleSaveEditedAnswer}
          onDelete={handleDeleteEditedAnswer}
          initialData={editingAnswer}
          staff={staff}
        />
      )}
    </div>
  );
};
